/**
 * useAzurePickerSelection — selection/focus state for the Azure picker. Prunes
 * both whenever the resource set is replaced (an identity switch re-keys the
 * discovery query; Refresh may change the set), so the footer count can never
 * exceed what confirm actually delivers. Identical refetch results are
 * structurally shared by TanStack Query (same reference — the effect skips)
 * and an in-flight fetch keeps the previous data, so a no-op or failed refetch
 * does not clear a still-valid selection.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';

export function useAzurePickerSelection(resources: AzureDiscoveredResource[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const valid = new Set(resources.map((r) => r.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setFocusedId((prev) => (prev !== undefined && valid.has(prev) ? prev : undefined));
  }, [resources]);

  const applySelection = useCallback(
    (ids: string[], checked: boolean) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (checked) next.add(id);
          else next.delete(id);
        }
        return next;
      }),
    []
  );

  const toggleResource = useCallback(
    (id: string, checked: boolean) => {
      setFocusedId(id);
      applySelection([id], checked);
    },
    [applySelection]
  );

  const resetSelection = useCallback(() => {
    setSelectedIds(new Set());
    setFocusedId(undefined);
  }, []);

  return { selectedIds, focusedId, setFocusedId, applySelection, toggleResource, resetSelection };
}
